/** @file Packs blocks per group (`entry.sheet`) into `packed-<group>` sheets with packSheets. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { packSheets } from '@zilo/pose3d-export/pack/packSheets';
import type { ExportDeps } from '@zilo/pose3d-export/types/exportDeps';
import type { PackBlock, PackedBlocks } from '@zilo/pose3d-export/types/packBlock';

/**
 * One `packSheets` call per group (first-appearance order) with `deps.padding` (default 1) and `deps.maxSide`
 * (default 4096); sheets are named `packed-<group>`, overflow sheets `packed-<group>-<n>` (n from 1); two groups
 * landing on the same name (`main` overflowing into group `main-1`'s sheet) throw `VALIDATION`.
 */
export function packBlocks(blocks: PackBlock[], deps: Pick<ExportDeps, 'packShelves' | 'maxSide' | 'padding'>): PackedBlocks {
  const groups = new Map<string, PackBlock[]>();
  for (const b of blocks) groups.set(b.group, [...(groups.get(b.group) ?? []), b]);
  const out: PackedBlocks = { sheets: {}, origins: {} };
  for (const [group, members] of groups) {
    const items = members.map((b) => ({ id: b.key, w: b.size[0], h: b.size[1] }));
    const { sheets } = packSheets(items, { maxSide: deps.maxSide ?? 4096, padding: deps.padding ?? 1 }, deps.packShelves);
    sheets.forEach((sheet, n) => {
      const name = n === 0 ? `packed-${group}` : `packed-${group}-${n}`;
      if (Object.hasOwn(out.sheets, name)) {
        throwError(ERROR_CODES.VALIDATION, { sheet: name, group }, { hint: `rename sheet group ${group}: its packed sheet name is taken` });
      }
      out.sheets[name] = [sheet.width, sheet.height];
      for (const r of sheet.rects) out.origins[r.id] = { sheet: name, origin: [r.x, r.y] };
    });
  }
  return out;
}
